import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, Users, Cake, UserPlus, Clock, Flame, Paperclip, Plus, MessageCircle, Gift, Eye } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { TEAM_DATA, TEAM_CHAT_RESPONSES } from '../../data/teamData';

const SEVERITY_STYLES = {
  dark: { high: 'bg-red-950/20 border-red-500/20', medium: 'bg-amber-950/15 border-amber-500/15', low: 'bg-[#0d1b2a] border-white/[0.06]' },
  light: { high: 'bg-red-50/60 border-red-200/60', medium: 'bg-amber-50/40 border-amber-200/50', low: 'bg-[#f0ece4] border-black/[0.06]' },
};

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const BurnoutCard = ({ person, isDark, onAction }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const styles = (isDark ? SEVERITY_STYLES.dark : SEVERITY_STYLES.light)[person.severity];
  const sevColor = person.severity === 'high' ? (isDark ? 'text-red-400' : 'text-red-600') : person.severity === 'medium' ? (isDark ? 'text-amber-400' : 'text-amber-600') : ts;
  return (
    <div className={`${styles} border rounded-xl p-3`} data-testid={`burnout-${person.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Flame className={`w-3 h-3 ${sevColor}`} />
          <span className={`text-[11px] font-semibold ${tp}`}>{person.name}</span>
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${person.severity === 'high' ? (isDark ? 'bg-red-500/15 text-red-400' : 'bg-red-50 text-red-600') : person.severity === 'medium' ? (isDark ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-50 text-amber-600') : (isDark ? 'bg-white/[0.04] text-white/50' : 'bg-black/[0.03] text-slate-500')}`}>{person.severity}</span>
        </div>
        <span className={`text-[9px] ${ts}`}>{person.department}</span>
      </div>
      <p className={`text-[10px] ${ts}`}>{person.role}</p>
      <p className={`text-[10px] mt-1 ${tp}`}>{person.signal}</p>
      <div className="flex items-center gap-3 mt-1.5">
        <span className={`text-[9px] ${ts}`}><Clock className="w-3 h-3 inline mr-0.5" /> PTO: {person.lastPTO}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
        <button onClick={() => onAction?.(person, person.action)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>{person.action}</button>
      </div>
    </div>
  );
};

const InsightCard = ({ item, isDark, agent }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>{agent} · {item.time}</p>
      </div>
    </div>
  );
};

const V4PeopleTeam = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const ctaStyle = `flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium whitespace-nowrap transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`;

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || TEAM_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleAction = (person, action) => {
    sendMessage(`${action} for ${person.name}.`, { text: `I'll set up "${action}" for ${person.name} (${person.role}, ${person.department}).\n\nBased on the burnout signals — ${person.signal.toLowerCase()} — this is timely. I'll coordinate with their manager and send you a confirmation.\n\nAnything else for this employee?` });
  };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll take care of "${label}" for ${detail}. Let me set that up and I'll send you a confirmation once it's done.\n\nAnything else you need?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Tali about your team..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="team-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.tali} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Tali</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="team-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="team-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="team-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Team</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.tali} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="team-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Team</p>
            <span className={`text-[10px] ${ts}`}>{TEAM_DATA.metrics[0].value} employees</span>
          </div>
          <button onClick={() => handleCta('Onboard New Member', 'Starting new hire onboarding workflow')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="team-onboard-btn">
            <Plus className="w-3.5 h-3.5" /> Onboard New Member
          </button>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          {TEAM_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Department table — compact grid layout */}
        <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl overflow-hidden`}>
          <div className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1.5fr] items-center px-4 py-2 border-b ${border}`}>
            <span className={`text-[9px] font-semibold ${ts}`}>Department</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>HC</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Open</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Attrition</span>
            <span className={`text-[9px] font-semibold text-center ${ts}`}>Burnout</span>
            <span className={`text-[9px] font-semibold text-right ${ts}`}>Lead</span>
          </div>
          {TEAM_DATA.departments.map(dept => (
            <div key={dept.name} className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1.5fr] items-center px-4 py-2 border-b ${border} last:border-0`} data-testid={`dept-${dept.name.toLowerCase()}`}>
              <span className={`text-[11px] font-medium ${tp}`}>{dept.name}</span>
              <span className={`text-[10px] text-center ${tp}`}>{dept.headcount}</span>
              <span className={`text-[10px] text-center ${dept.openRoles > 2 ? (isDark ? 'text-amber-400' : 'text-amber-600') : tp}`}>{dept.openRoles}</span>
              <span className={`text-[10px] text-center ${dept.attrition > 10 ? (isDark ? 'text-red-400' : 'text-red-500') : tp}`}>{dept.attrition}%</span>
              <span className={`text-[10px] text-center ${dept.burnoutRisk > 0 ? (isDark ? 'text-amber-400' : 'text-amber-500') : ts}`}>{dept.burnoutRisk}</span>
              <div className="flex items-center justify-end gap-2">
                <span className={`text-[10px] ${ts}`}>{dept.lead}</span>
                <button onClick={() => handleCta('View Department', dept.name)} className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                  <Eye className="w-3 h-3" /> View
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Two columns: Burnout alerts + Recent & Anniversaries */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Flame className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Burnout Risk Alerts</p>
              <span className={`text-[9px] ${ts}`}>{TEAM_DATA.burnoutAlerts.length}</span>
            </div>
            <div className="flex flex-col gap-3">
              {TEAM_DATA.burnoutAlerts.slice(0, 4).map(b => <BurnoutCard key={b.id} person={b} isDark={isDark} onAction={handleAction} />)}
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <UserPlus className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  <p className={`text-xs font-semibold ${tp}`}>Recent Joiners</p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {TEAM_DATA.recentJoiners.map((j, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
                    <div><p className={`text-[11px] font-medium ${tp}`}>{j.name}</p><p className={`text-[9px] ${ts}`}>{j.role} · {j.department}</p></div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] ${ts}`}>{j.joinDate}</span>
                      <button onClick={() => handleCta('Setup 1:1', j.name)} className={ctaStyle}>
                        <MessageCircle className="w-3 h-3" /> 1:1
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Cake className={`w-4 h-4 ${isDark ? 'text-pink-400' : 'text-pink-600'}`} />
                <p className={`text-xs font-semibold ${tp}`}>Upcoming Anniversaries</p>
              </div>
              <div className="flex flex-col gap-2">
                {TEAM_DATA.upcomingAnniversaries.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
                    <div><p className={`text-[11px] font-medium ${tp}`}>{a.name}</p><p className={`text-[9px] ${ts}`}>{a.role} · {a.years} years</p></div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-medium ${isDark ? 'text-pink-400' : 'text-pink-600'}`}>{a.date}</span>
                      <button onClick={() => handleCta('Send Recognition Note', a.name)} className={ctaStyle}>
                        <Gift className="w-3 h-3" /> Recognize
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.tali} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Tali's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {TEAM_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} agent="Tali" />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4PeopleTeam;
